function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function sortTreeItems(items) {
  return [...items].sort((a, b) => {
    const ao = Number(a.order || 0);
    const bo = Number(b.order || 0);
    if (ao !== bo) return ao - bo;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });
}

export function hasTreeStructure(items = []) {
  return items.some((it) => it.kind === 'index' || it.parentTopicId);
}

export function firstContentItem(items = []) {
  return items.find((it) => it && it.kind !== 'index' && it.html);
}

export function renderContentTree(items = [], selectedTopicId = null) {
  const visibleItems = items.filter((it) => it && it.topicId);
  const byParent = new Map();
  visibleItems.forEach((item) => {
    const key = item.parentTopicId || '';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(item);
  });

  function renderNode(item, depth) {
    const isIndex = item.kind === 'index';
    const children = sortTreeItems(byParent.get(item.topicId) || []);
    const active = selectedTopicId && item.topicId === selectedTopicId ? ' active' : '';
    if (isIndex) {
      return `
        <div class="readonly-tree-item readonly-tree-folder" data-folder-id="${escapeHtml(item.topicId)}" style="--depth:${depth};" aria-expanded="false">
          <span class="readonly-tree-caret" aria-hidden="true">›</span>
          <span class="readonly-tree-title">📁 ${escapeHtml(item.title || 'Folder')}</span>
        </div>
        <div class="readonly-tree-children" data-parent-id="${escapeHtml(item.topicId)}" hidden>
          ${children.map((child) => renderNode(child, depth + 1)).join('')}
        </div>
      `;
    }
    return `
      <button class="readonly-tree-item readonly-tree-topic${active}" data-topic-id="${escapeHtml(item.topicId)}" style="--depth:${depth};" type="button">
        <span class="readonly-tree-title">🧩 ${escapeHtml(item.title || 'Topic')}</span>
      </button>
      ${children.length ? `<div class="readonly-tree-children">${children.map((child) => renderNode(child, depth + 1)).join('')}</div>` : ''}
    `;
  }

  const roots = sortTreeItems(byParent.get('') || []);
  return `<div class="readonly-content-tree">${roots.map((item) => renderNode(item, 0)).join('')}</div>`;
}

export function wireContentTree(container, items = [], { initialTopicId = null, onSelect } = {}) {
  const byId = new Map(items.map((item) => [item.topicId, item]));
  let selectedTopicId = initialTopicId || firstContentItem(items)?.topicId || null;

  function openFolder(folderId, open) {
    const folder = container.querySelector(`[data-folder-id="${CSS.escape(folderId)}"]`);
    const childWrap = container.querySelector(`[data-parent-id="${CSS.escape(folderId)}"]`);
    if (!folder || !childWrap) return;
    const nextOpen = open ?? childWrap.hidden;
    childWrap.hidden = !nextOpen;
    folder.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
  }

  function openAncestors(topicId) {
    let cur = byId.get(topicId);
    const seen = new Set();
    while (cur?.parentTopicId && !seen.has(cur.parentTopicId)) {
      seen.add(cur.parentTopicId);
      openFolder(cur.parentTopicId, true);
      cur = byId.get(cur.parentTopicId);
    }
  }

  function selectTopic(topicId) {
    const item = byId.get(topicId);
    if (!item || item.kind === 'index' || !item.html) return;
    selectedTopicId = topicId;
    container.querySelectorAll('.readonly-tree-topic').forEach((el) => {
      el.classList.toggle('active', el.dataset.topicId === selectedTopicId);
    });
    openAncestors(selectedTopicId);
    onSelect?.(item);
  }

  container.querySelectorAll('[data-folder-id]').forEach((folder) => {
    folder.addEventListener('click', () => openFolder(folder.dataset.folderId));
  });
  container.querySelectorAll('[data-topic-id]').forEach((topic) => {
    topic.addEventListener('click', () => selectTopic(topic.dataset.topicId));
  });

  if (selectedTopicId) selectTopic(selectedTopicId);
  return { selectTopic };
}
